import { Capacitor } from '@capacitor/core';
import {
  Alert,
  AlertTitle,
  Badge,
  Button,
  Chip,
  Typography,
} from '@mui/material';
import { InlineStatus } from 'components/InlineStatus';
import { StatTable } from 'components/StatTable';
import {
  MapEntityDetailsLayout,
  useMapEntityDetailsTabs,
} from 'features/mapping/components/MapEntityDetailsLayout';
import { MapEntityDrawerContext } from 'features/mapping/components/MapEntityDrawer';
import { BannerPlacement, SupportUsBanner } from 'components/SupportUsBanner';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent';
import { WildfireAutomatedDisclaimer } from 'features/wildfires/components/WildfireAutomatedDisclaimer';
import { GeoEventEvacuationsButton } from 'features/geoEvents/components/GeoEventEvacuationsButton';
import { GeoEventNotificationsButton } from 'features/geoEvents/components/GeoEventNotificationsButton';
import { GeoEventUpdateTime } from 'features/geoEvents/components/GeoEventUpdateTime';
import { GeoEventCreatedByLine } from 'features/geoEvents/components/GeoEventMetadataRows';
import { geoEventShareButtonSx } from 'features/geoEvents/geoEvents.utils';
import { shareGeoEvent } from 'features/geoEvents/geoEvent.utils';
import { WildfireStoppedInfo } from 'features/wildfires/components/WildfireStoppedInfo';
import { Locations } from 'features/locations/components/Locations';
import { GeoEventReports } from 'features/reports/components/GeoEventReports';
import { WildfireStructuredInfo } from 'features/wildfires/components/WildfireStructuredInfo';
import { useProgressionSourcesWithModels } from 'features/wildfires/hooks/useProgressionSourcesWithModels';
import { useSelectedIncidentGeoEventId } from 'features/geoEvents/hooks/useSelectedIncidentGeoEventId';
import { useInternalIncidentTab } from 'features/geoEvents/hooks/useInternalIncidentTab';
import { useWildfireBanner } from 'features/wildfires/hooks/useWildfireBanner';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { useShareOrCopy } from 'hooks/useShareOrCopy';
import { useTabs } from 'hooks/useTabs';
import { useContext, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { getAcreageStrParts, getRegionsDisplay } from 'shared/utils';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Link } from 'react-router-dom';
import { useProgressionModelsEnabled } from 'features/wildfires/hooks/useProgressionModelsEnabled';
import { ForcedTheme } from 'components/ForcedTheme';
import { UserStatusChip } from 'components/UserStatusChip';
import {
  GeoEventEvacuationInfo,
  hasGeoEventEvacuationInfo,
} from 'features/evacZones/components/GeoEventEvacuationInfo';
import { useShowNewProgressionModelAlert } from './useShowNewProgressionModelAlert';
import { getTimeAgo } from '../../shared/dates';
import { getChildLocations } from './WildfireDetails.utils';

const TAB_PANEL_ID_PREFIX = 'incident-details';

const BASE_TABS_TOP = 240;

export const WildfireDetails = (): JSX.Element => {
  const geoEventId = useSelectedIncidentGeoEventId();
  const { geoEvent, isGeoEventLoading, isGeoEventFetchError } =
    useGeoEvent<Wildfire>({
      id: geoEventId,
      useCacheBuster: true,
    });
  const { t, i18n } = useTranslation();
  const { canShareOrCopy, shareOrCopyUrl } = useShareOrCopy();
  const { showBanner, handleClose } = useWildfireBanner({ wildfire: geoEvent });
  const showProgressionModels = useProgressionModelsEnabled();
  const internalTab = useInternalIncidentTab(geoEvent);
  const { progressionSourcesWithModels } = useProgressionSourcesWithModels({
    wildfireId: geoEventId,
  });

  const { showNewProgressionModelAlert, progressionModelLastUpdated } =
    useShowNewProgressionModelAlert({
      progressionModels: progressionSourcesWithModels.flatMap(
        (source) => source.models,
      ),
    });

  const shelterLocations = getChildLocations(geoEvent);

  const { scrollElementRef } = useContext(MapEntityDrawerContext);

  const availableModels = progressionSourcesWithModels.filter(
    (source) => source.models.length > 0,
  ).length;

  const tabData = [
    {
      tabId: 'updates',
      label: t('geoEvent.tabs.updates'),
      enabled: true,
      content: geoEventId && (
        <GeoEventReports geoEventId={geoEventId} geoEventType="wildfire" />
      ),
    },
    {
      tabId: 'shelters',
      label: t('geoEvent.tabs.shelters'),
      adornment: <Badge badgeContent={shelterLocations.length} />,
      enabled: shelterLocations.length > 0,
      content: geoEvent && <Locations locations={shelterLocations} />,
    },
    {
      tabId: 'info',
      label: t('geoEvent.tabs.info'),
      adornment:
        showProgressionModels && availableModels ? (
          <Badge badgeContent={availableModels} />
        ) : undefined,
      enabled: true,
      content: geoEvent && <WildfireStructuredInfo wildfire={geoEvent} />,
    },
    internalTab,
  ];

  const evacInfoContainerRef = useRef<HTMLDivElement>(null);
  const progressionModelAlertRef = useRef<HTMLDivElement>(null);
  const supportUsBannerRef = useRef<HTMLDivElement>(null);

  const { onChangeTab } = useMapEntityDetailsTabs({
    baseTop: BASE_TABS_TOP,
    containerRefs: {
      evacInfo: evacInfoContainerRef,
      progressionModelAlert: progressionModelAlertRef,
      supportUsBanner: supportUsBannerRef,
    },
    // Extra offset for the native-only notification button.
    nativeOffset: Capacitor.isNativePlatform() ? 64 : 0,
  });

  const { tabsProps, tabProps, tabPanels } = useTabs({
    idPrefix: TAB_PANEL_ID_PREFIX,
    ariaLabel: t('geoEvent.tabsLabel'),
    tabData,
    onChangeTab,
  });

  const handleTop = (): void => {
    if (!scrollElementRef) {
      return;
    }

    scrollElementRef.current?.scrollTo({ behavior: 'smooth', top: 0 });
  };

  if (isGeoEventLoading || isGeoEventFetchError || !geoEvent || !geoEventId) {
    return <InlineStatus status={isGeoEventLoading ? 'loading' : 'error'} />;
  }

  const complexName = geoEvent.parentGeoEvents?.[0]?.name;

  const {
    name,
    dateCreated,
    dateModified,
    data: { acreage, containment, isFps },
    address,
    isActive,
    createdByName,
  } = geoEvent;

  const [acreageStr] = getAcreageStrParts(
    acreage,
    containment,
    i18n.resolvedLanguage,
  );

  const stats = [
    {
      label: t('geoEvent.acres'),
      value: acreageStr,
      testId: 'acresBox',
    },
    {
      label: t('geoEvent.containment'),
      value: typeof containment === 'number' ? `${containment}%` : null,
      testId: 'containmentBox',
    },
  ];

  const handleShare = (): void => {
    shareGeoEvent({ name, address, dateModified, shareOrCopyUrl, t });
  };

  return (
    <>
      <Helmet>
        <title>{t('home.wildfireTitle', { name: geoEvent.name })}</title>
      </Helmet>

      <MapEntityDetailsLayout.Root>
        <MapEntityDetailsLayout.Header
          title={
            <>
              {geoEvent.name}
              {complexName && (
                <ForcedTheme mode="light">
                  <Chip
                    variant="watchduty"
                    label={complexName}
                    sx={{
                      marginLeft: 1,
                      backgroundColor: 'watchdutyComplex.main',
                    }}
                  />
                </ForcedTheme>
              )}
            </>
          }
          subtitle={
            geoEvent.address ? (
              <Typography data-testid="geoevent-address">
                {geoEvent.address}
              </Typography>
            ) : undefined
          }
          hideSubtitleWhenSticky
          caption={
            <Typography
              variant="body2"
              color="textSecondary"
              data-testid="geoevent-regions"
            >
              {getRegionsDisplay(geoEvent.regions)}
            </Typography>
          }
        />

        <MapEntityDetailsLayout.Body>
          <StatTable stats={stats} itemSize={6} />

          <MapEntityDetailsLayout.Meta data-testid="createdBy">
            {isFps && <WildfireStoppedInfo />}

            <GeoEventUpdateTime geoEvent={geoEvent} />

            <GeoEventCreatedByLine
              createdByName={createdByName}
              dateCreated={dateCreated}
              isActive={isActive}
            />
          </MapEntityDetailsLayout.Meta>

          {(Capacitor.isNativePlatform() || canShareOrCopy) && (
            <MapEntityDetailsLayout.Actions>
              {Capacitor.isNativePlatform() && (
                <GeoEventNotificationsButton geoEvent={geoEvent} />
              )}

              {canShareOrCopy && (
                <Button
                  fullWidth
                  color="neutral"
                  variant="outlined"
                  onClick={handleShare}
                  size="large"
                  sx={geoEventShareButtonSx}
                >
                  {t('geoEvent.shareIncident')}
                </Button>
              )}
            </MapEntityDetailsLayout.Actions>
          )}
        </MapEntityDetailsLayout.Body>

        {(!geoEvent.reporterManaged || hasGeoEventEvacuationInfo(geoEvent)) && (
          <MapEntityDetailsLayout.Body containerRef={evacInfoContainerRef}>
            {geoEvent.reporterManaged ? (
              <GeoEventEvacuationInfo geoEvent={geoEvent} />
            ) : (
              <WildfireAutomatedDisclaimer wildfire={geoEvent} />
            )}
          </MapEntityDetailsLayout.Body>
        )}

        {showNewProgressionModelAlert && progressionModelLastUpdated && (
          <MapEntityDetailsLayout.Body
            pb={2}
            containerRef={progressionModelAlertRef}
          >
            <Link
              to={`${window.location.pathname}?tab=info`}
              style={{ textDecoration: 'none' }}
            >
              <Alert
                icon={false}
                action={
                  <ArrowForwardIosIcon
                    fontSize="small"
                    color="action"
                    sx={{ pointerEvents: 'none' }}
                  />
                }
                severity="watchduty-neutral"
                slotProps={{
                  action: {
                    sx: { alignItems: 'center' },
                  },
                }}
              >
                <AlertTitle>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                    }}
                  >
                    {t('wildfireProgressionModel.newFireProgressionModel')}
                    <UserStatusChip type="pro" sx={{ marginLeft: 1 }} />
                  </Typography>
                </AlertTitle>
                <Typography variant="subtitle1">
                  {t('common.updated')}&nbsp;
                  {getTimeAgo(progressionModelLastUpdated.toISOString())}
                </Typography>
              </Alert>
            </Link>
          </MapEntityDetailsLayout.Body>
        )}

        {showBanner && (
          <MapEntityDetailsLayout.Body containerRef={supportUsBannerRef}>
            <SupportUsBanner
              placement={BannerPlacement.GEO_EVENT}
              geoEventId={geoEventId}
              onClose={handleClose}
            />
          </MapEntityDetailsLayout.Body>
        )}

        <MapEntityDetailsLayout.Tabs
          tabsProps={tabsProps}
          tabProps={tabProps}
          tabPanels={tabPanels}
          topSlot={
            <GeoEventEvacuationsButton
              geoEvent={geoEvent}
              handleTop={handleTop}
            />
          }
          dontUpdateHolderHeightWhenSticky
        />
      </MapEntityDetailsLayout.Root>
    </>
  );
};
